import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  List,
  message,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  AppstoreAddOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import { hasRichText, richTextMarkup } from "./richTextUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const PAGE_SIZE = 20;

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || "").trim());

const displayText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim() && !isUuid(value)) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const namedRefLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return isUuid(value) ? "" : titleCase(value);
  return displayText(value.name, value.label, value.title, value.code);
};

const normalizeStrings = (values) => {
  if (!values) return [];
  const list = Array.isArray(values) ? values : Array.from(values instanceof Set ? values : [values]);
  return list
    .map((value) => (value && typeof value === "object" ? namedRefLabel(value) : titleCase(value)))
    .filter(Boolean);
};

const compactList = (...groups) => groups.flat().filter(Boolean);

const locationLabel = (location) => {
  if (!location) return "";
  if (typeof location === "string") return location;
  const direct = displayText(location.label, location.name, location.address, location.formattedAddress);
  if (direct) return direct;
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.state,
    location.zipCode,
    location.country,
  ].filter(Boolean).join(", ");
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value || "");
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString()}`;
  }
};

const salaryLabel = (job) => {
  const direct = firstValue(job?.salaryExpectation, job?.salaryExpectations, job?.salaryRange, job?.compensation, job?.pay, job?.payRange);
  if (direct) return String(direct);
  const currency = job?.currency || job?.salaryCurrency || job?.payCurrency || "USD";
  const min = firstValue(job?.salaryMin, job?.minSalary, job?.minimumSalary, job?.payMin, job?.minPay);
  const max = firstValue(job?.salaryMax, job?.maxSalary, job?.maximumSalary, job?.payMax, job?.maxPay);
  const single = firstValue(job?.salary, job?.payRate, job?.hourlyRate);
  const cadence = titleCase(firstValue(job?.salaryPeriod, job?.payPeriod, job?.rateUnit, job?.payFrequency));
  const suffix = cadence ? ` / ${cadence.toLowerCase()}` : "";
  if (min && max) return `${formatMoney(min, currency)} - ${formatMoney(max, currency)}${suffix}`;
  if (min) return `From ${formatMoney(min, currency)}${suffix}`;
  if (max) return `Up to ${formatMoney(max, currency)}${suffix}`;
  if (single) return `${formatMoney(single, currency)}${suffix}`;
  return "Not set";
};

const statusColor = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["PUBLISHED", "ACTIVE", "OPEN"].includes(normalized)) return "green";
  if (normalized === "DRAFT" || normalized === "SUBMITTED") return "gold";
  if (["CLOSED", "ARCHIVED", "CANCELLED", "HIRED"].includes(normalized)) return "default";
  return "blue";
};

const isOwnedJobPost = (item, userId, workspaceId) => {
  const posterId = firstText(item?.posterId, item?.poster?.id);
  const itemWorkspaceId = firstText(item?.workspaceId, item?.workspace?.id);
  return Boolean((posterId && userId && posterId === userId) || (itemWorkspaceId && workspaceId && itemWorkspaceId === workspaceId));
};

const jobPostEditPath = (jobId) => `${paths.newJobPost}?jobOpportunityId=${encodeURIComponent(jobId)}`;

export default function JobPosts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastPage, setLastPage] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [drawerJob, setDrawerJob] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState("");
  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  const postStats = useMemo(() => {
    const active = posts.filter((post) => ["PUBLISHED", "ACTIVE", "OPEN"].includes(String(post.status || "").toUpperCase())).length;
    const drafts = posts.filter((post) => String(post.status || "").toUpperCase() === "DRAFT").length;
    return { active, drafts };
  }, [posts]);

  const loadPosts = useCallback(async (reset = false) => {
    if (loadingRef.current || workspaceLoading) return;
    if (!selectedWorkspaceId || !actorId) return;
    try {
      loadingRef.current = true;
      setLoading(true);
      setError("");
      const page = reset ? 0 : pageRef.current;
      const result = await olreadiaService.jobOpportunities.listEmployer({
        actorId,
        posterId: actorId,
        workspaceId: selectedWorkspaceId,
        page,
        size: PAGE_SIZE,
      });
      const content = result.content || [];
      setPosts((current) => {
        const merged = reset ? content : [...current, ...content];
        const seen = new Set();
        return merged.filter((post) => {
          const id = post.id || `${post.title}-${post.createdAt}-${post.updatedAt}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });
      setLastPage(Boolean(result.last) || content.length < PAGE_SIZE);
      pageRef.current = page + 1;
    } catch (nextError) {
      if (reset) setPosts([]);
      setError(nextError?.message || "Failed to load job posts.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [actorId, selectedWorkspaceId, workspaceLoading]);

  useEffect(() => {
    pageRef.current = 0;
    loadPosts(true);
  }, [loadPosts]);

  const openDrawer = (post) => {
    setSelectedPost(post);
    setDrawerJob(post);
    setDrawerError("");
  };

  const closeDrawer = () => {
    setSelectedPost(null);
    setDrawerJob(null);
    setDrawerError("");
  };

  useEffect(() => {
    let cancelled = false;
    const postId = selectedPost?.id;
    if (!postId) return undefined;
    const loadDetails = async () => {
      try {
        setDrawerLoading(true);
        setDrawerError("");
        const next = await olreadiaService.jobOpportunities.get(postId, actorId);
        if (!cancelled) setDrawerJob(next);
      } catch (nextError) {
        if (!cancelled) setDrawerError(nextError?.message || "Could not load job post details.");
      } finally {
        if (!cancelled) setDrawerLoading(false);
      }
    };
    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [actorId, selectedPost?.id]);

  const shareJobPost = async (post) => {
    if (!post?.id) {
      message.warning("This job post is missing a shareable ID.");
      return;
    }
    const url = `${window.location.origin}${paths.jobOpportunityDetails(encodeURIComponent(post.id))}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title || "Olreadia job post", url });
      } else {
        await navigator.clipboard.writeText(url);
        message.success("Job post link copied.");
      }
    } catch (nextError) {
      if (nextError?.name !== "AbortError") message.error(nextError?.message || "Unable to share this job post.");
    }
  };

  const editJobPost = useCallback((post) => {
    const jobId = post?.id;
    if (!jobId) {
      message.warning("This job post is missing an edit ID.");
      return;
    }
    navigate(jobPostEditPath(jobId), { state: { jobOpportunityId: jobId, jobPost: post } });
  }, [navigate]);

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><SolutionOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Jobs & Talent</Text>
              <Title level={2}>Job Posts</Title>
              <Paragraph>{loading && posts.length === 0 ? "Loading..." : `${posts.length} posts • ${postStats.active} active • ${postStats.drafts} drafts`}</Paragraph>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadPosts(true)}>Refresh</Button>
              <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => navigate(paths.newJobPost)}>Post a Job</Button>
            </Space>
          </section>

          {error ? <Alert type="error" showIcon message="Unable to load job posts" description={error} action={<Button onClick={() => loadPosts(true)}>Retry</Button>} style={{ marginBottom: 12 }} /> : null}

          {!selectedWorkspaceId && !workspaceLoading ? (
            <Card className="olreadia-flow-card"><Empty description="Select a workspace to view job posts." /></Card>
          ) : null}

          {loading && posts.length === 0 ? <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 6 }} /></Card> : null}

          {!loading && !posts.length && selectedWorkspaceId ? (
            <Card className="olreadia-flow-card">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No job posts yet">
                <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => navigate(paths.newJobPost)}>Post a Job</Button>
              </Empty>
            </Card>
          ) : null}

          {posts.length ? (
            <List
              dataSource={posts}
              renderItem={(post) => (
                <JobPostCard
                  key={post.id}
                  post={post}
                  actorId={actorId}
                  workspaceId={selectedWorkspaceId}
                  onOpen={() => openDrawer(post)}
                  onEdit={() => editJobPost(post)}
                />
              )}
            />
          ) : null}

          {posts.length && !lastPage ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <Button type="primary" loading={loading} onClick={() => loadPosts(false)}>Load more</Button>
            </div>
          ) : null}
        </main>
        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

      <JobPostDrawer
        job={drawerJob}
        open={Boolean(selectedPost)}
        loading={drawerLoading}
        error={drawerError}
        onClose={closeDrawer}
        onShare={shareJobPost}
        onEdit={editJobPost}
      />
    </div>
  );
}

function JobPostCard({ post, actorId, workspaceId, onOpen, onEdit }) {
  const skillLabels = compactList(normalizeStrings(post.requiredSkills), normalizeStrings(post.otherRequiredSkills));
  const scheduleLabel = compactList(normalizeStrings(post.workSchedules), post.workSchedule ? [titleCase(post.workSchedule)] : []).join(", ");
  const locationLabels = Array.isArray(post.requiredLocations) ? post.requiredLocations.map(locationLabel).filter(Boolean) : [];
  const businessName = namedRefLabel(post.workspace);
  const posterName = namedRefLabel(post.poster);
  const educationLabel = titleCase(post.requiredEducationLevel);
  const experienceLabel = post.requiredYearsOfExperience != null ? `${post.requiredYearsOfExperience}+ yrs` : "";
  const payLabel = salaryLabel(post);
  const cardProperties = [
    { value: formatDate(post.createdAt), icon: <CalendarOutlined /> },
    { value: locationLabels.slice(0, 2).join(", "), icon: <EnvironmentOutlined /> },
    { value: skillLabels.slice(0, 3).join(", "), icon: <AppstoreAddOutlined /> },
    { value: titleCase(post.locationType), icon: <EnvironmentOutlined /> },
    { value: scheduleLabel, icon: <FileTextOutlined /> },
    { value: experienceLabel, icon: <CheckCircleOutlined /> },
    { value: educationLabel, icon: <SolutionOutlined /> },
    { value: payLabel === "Not set" ? "" : payLabel, icon: <DollarOutlined /> },
  ].filter((item) => item.value);
  const canEdit = isOwnedJobPost(post, actorId, workspaceId);

  return (
    <Card className="olreadia-flow-card olreadia-job-post-card" hoverable onClick={onOpen} style={{ marginBottom: 8 }}>
      <div className="olreadia-flow-card__top">
        <span className="olreadia-flow-card__icon"><SolutionOutlined /></span>
        <div style={{ minWidth: 0 }}>
          <Title level={4}>{post.title || "Untitled job post"}</Title>
          <Text type="secondary">{businessName || posterName || titleCase(post.posterType) || "Employer post"}</Text>
        </div>
        <Space wrap>
          <Tag color={statusColor(post.status)}>{titleCase(post.status || "DRAFT")}</Tag>
          {canEdit ? (
            <Button
              icon={<EditOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
            />
          ) : null}
        </Space>
      </div>
      <div className="olreadia-job-post-card__properties">
        {cardProperties.slice(0, 6).map((item) => (
          <span className="olreadia-job-post-card__property" key={`${item.value}`}>
            <span className="olreadia-job-post-card__property-icon">{item.icon}</span>
            <span className="olreadia-job-post-card__property-copy">{item.value}</span>
          </span>
        ))}
      </div>
      <div className="olreadia-job-post-card__footer">
        {(locationLabels[0] || skillLabels.slice(0, 2).join(", ")) ? (
          <Text className="olreadia-job-post-card__location">
            <EnvironmentOutlined /> {locationLabels[0] || skillLabels.slice(0, 2).join(", ")}
          </Text>
        ) : <span />}
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          View
        </Button>
      </div>
    </Card>
  );
}

function JobPostDrawer({ job, open, loading, error, onClose, onShare, onEdit }) {
  const skills = useMemo(() => compactList(normalizeStrings(job?.requiredSkills), normalizeStrings(job?.otherRequiredSkills)), [job]);
  const specializations = useMemo(() => compactList(normalizeStrings(job?.requiredSpecializations), normalizeStrings(job?.otherRequiredSpecializations)), [job]);
  const workAreas = useMemo(() => compactList(normalizeStrings(job?.requiredWorkAreas), normalizeStrings(job?.otherRequiredWorkAreas)), [job]);
  const schedules = useMemo(() => compactList(normalizeStrings(job?.workSchedules), job?.workSchedule ? [titleCase(job.workSchedule)] : []), [job]);
  const locations = useMemo(() => (Array.isArray(job?.requiredLocations) ? job.requiredLocations.map(locationLabel).filter(Boolean) : []), [job]);

  const detailItems = [
    { label: "Posted", value: formatDateTime(job?.createdAt), icon: <CalendarOutlined /> },
    { label: "Application window", value: `${formatDate(job?.postStartDate)} - ${formatDate(job?.postEndDate)}`, icon: <CalendarOutlined /> },
    { label: "Location type", value: titleCase(job?.locationType) || "Not set", icon: <EnvironmentOutlined /> },
    { label: "Schedule", value: schedules.join(", ") || "Not set", icon: <FileTextOutlined /> },
    { label: "Salary", value: salaryLabel(job), icon: <DollarOutlined /> },
    { label: "Experience", value: job?.requiredYearsOfExperience != null ? `${job.requiredYearsOfExperience}+ years` : "Not set", icon: <CheckCircleOutlined /> },
    { label: "Education", value: titleCase(job?.requiredEducationLevel) || "Not set", icon: <CheckCircleOutlined /> },
  ];

  const textSections = [
    { label: "Description", value: job?.description },
    { label: "Responsibilities", value: job?.responsibilities },
    { label: "Qualifications", value: job?.qualifications },
    { label: "Requirements", value: job?.requirements },
    { label: "Benefits", value: job?.benefits },
    { label: "How to apply", value: firstText(job?.applicationInstructions, job?.howToApply, job?.applyInstructions) },
  ].filter((item) => hasRichText(item.value));

  return (
    <Drawer title="Job post details" open={open} onClose={onClose} width="min(560px, 100vw)" className="olreadia-flow-drawer" destroyOnClose>
      <Space direction="vertical" size={14} className="olreadia-flow-drawer__stack">
        {loading ? <Card className="olreadia-flow-card"><Skeleton active paragraph={{ rows: 5 }} /></Card> : null}
        {error ? <Alert type="warning" showIcon message="Unable to load latest details" description={error} /> : null}
        {job ? (
          <>
            <Card className="olreadia-flow-card">
              <div className="olreadia-flow-card__top">
                <span className="olreadia-flow-card__icon"><SolutionOutlined /></span>
                <div style={{ minWidth: 0 }}>
                  <Title level={4}>{job.title || "Untitled job post"}</Title>
                  <Text type="secondary">{namedRefLabel(job.workspace) || namedRefLabel(job.poster) || titleCase(job.posterType) || "Employer post"}</Text>
                </div>
                <Tag color={statusColor(job.status)}>{titleCase(job.status || "DRAFT")}</Tag>
              </div>
              <Space wrap className="olreadia-job-post-drawer__actions">
                <Button className="olreadia-job-post-drawer__manage-button" type="primary" icon={<EyeOutlined />} href={job.id ? paths.jobOpportunityDetails(job.id) : undefined}>Manage</Button>
                <Button icon={<EditOutlined />} onClick={() => onEdit(job)}>Edit</Button>
                <Button icon={<TeamOutlined />} href={job.id ? `${paths.jobApplications}?context=hiring&jobOpportunityId=${encodeURIComponent(job.id)}` : undefined}>Applications</Button>
                <Button icon={<ShareAltOutlined />} onClick={() => onShare(job)}>Share</Button>
              </Space>
            </Card>

            <Card className="olreadia-flow-card">
              <Title level={4}>Overview</Title>
              <div className="olreadia-flow-detail-grid">
                {detailItems.map((item) => (
                  <div key={item.label}>
                    <Text>{item.icon} {item.label}</Text>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </Card>

            {textSections.map((section) => (
              <Card key={section.label} className="olreadia-flow-card">
                <Title level={4}>{section.label}</Title>
                <div
                  className="olreadia-rich-text"
                  dangerouslySetInnerHTML={richTextMarkup(section.value)}
                />
              </Card>
            ))}

            <Card className="olreadia-flow-card">
              <Title level={4}>Requirements</Title>
              <Space wrap size={[6, 6]}>
                {[...skills, ...specializations, ...workAreas].map((item) => <Tag key={item}>{item}</Tag>)}
                {!skills.length && !specializations.length && !workAreas.length ? <Text type="secondary">No requirements listed.</Text> : null}
              </Space>
            </Card>

            <Card className="olreadia-flow-card">
              <Title level={4}>Locations</Title>
              <Space wrap size={[6, 6]}>
                {locations.map((item) => <Tag key={item}>{item}</Tag>)}
                {!locations.length ? <Text type="secondary">No locations listed.</Text> : null}
              </Space>
            </Card>
          </>
        ) : null}
      </Space>
    </Drawer>
  );
}
